// Utility function to convert a string to a number if possible
export const convertToNumber = (value) => {
  if (!value || typeof value !== 'string') return value;

  // Clean the value (remove spaces, replace commas with dots for decimals)
  const cleanValue = value.trim().replace(/\s+/g, '').replace(',', '.');

  // Check if it's a number (integer or decimal)
  // Regex for numbers: optional sign +/-, digits, optional dot and digits
  const numberRegex = /^[+-]?\d+\.?\d*$/;

  if (numberRegex.test(cleanValue)) {
    const num = parseFloat(cleanValue);
    // Verify that the conversion is valid
    if (!isNaN(num) && isFinite(num)) {
      return num;
    }
  }

  // If it's not a number, return the original value
  return value;
};

// Helper function to detect if a line is a markdown table separator
const isSeparatorLine = (line) => {
  if (!line || typeof line !== 'string') return false;

  // Remove leading/trailing pipes and spaces
  const trimmed = line.trim();
  if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) return false;

  // Check if the line contains only pipes, dashes, spaces, and colons
  const content = trimmed.slice(1, -1); // Remove outer pipes
  const parts = content.split('|');

  // Each part should contain only dashes, spaces, and optionally colons (for alignment)
  return parts.every((part) => /^[-\s:]*$/.test(part.trim()));
};

// Custom function to parse markdown tables
export const parseMarkdownTable = (markdown) => {
  if (!markdown) return [];

  const lines = markdown
    .trim()
    .split('\n')
    .filter((line) => line.trim());
  if (lines.length < 2) return [];

  const headerLine = lines[0];
  let dataLines;

  // Check if the second line is a separator line
  if (lines.length > 1 && isSeparatorLine(lines[1])) {
    // Skip the separator line
    dataLines = lines.slice(2);
  } else {
    // No separator line, data starts immediately after header
    dataLines = lines.slice(1);
  }

  // Parse headers
  const headers = headerLine
    .split('|')
    .map((cell) => cell.trim())
    .filter((cell) => cell !== '');

  // Parse data
  const data = dataLines.map((line) => {
    const cells = line
      .split('|')
      .map((cell) => cell.trim())
      .filter((cell) => cell !== '');

    const row = {};
    headers.forEach((header, index) => {
      const cellValue = cells[index] || '';
      // Convert to number if possible, otherwise keep as text
      row[header] = convertToNumber(cellValue);
    });
    return row;
  });

  return data;
};
